import React, { useEffect, useState } from 'react';

const DEFAULT_MESSAGE = "We're currently down for scheduled maintenance.";
const RECHECK_MS = 60 * 1000;

/**
 * Build-time maintenance notice, shown site-wide from AuthWrapper.
 *
 * REACT_APP_MAINTENANCE_UNTIL (ISO 8601, UTC) is the switch: the banner shows only while it parses
 * and is in the future, so it clears itself once that time passes. REACT_APP_MAINTENANCE_MESSAGE is
 * optional wording; without it the default message is used. No time, or an unparseable one, means
 * no banner, so a leftover message can never stick around.
 */
export const getMaintenanceNotice = (message, until, now = Date.now()) => {
  if (!until) {
    return null;
  }
  const untilMs = Date.parse(until);
  if (Number.isNaN(untilMs) || untilMs <= now) {
    return null;
  }
  const text = (message || '').trim() || DEFAULT_MESSAGE;
  return { message: text, until: new Date(untilMs) };
};

// The viewer's local date and time, with their time zone so "back by" is unambiguous.
export const formatLocalTime = date =>
  date.toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });

const MaintenanceBanner = ({
  message = process.env.REACT_APP_MAINTENANCE_MESSAGE,
  until = process.env.REACT_APP_MAINTENANCE_UNTIL,
}) => {
  const [now, setNow] = useState(() => Date.now());
  const notice = getMaintenanceNotice(message, until, now);
  const active = notice !== null;

  // Re-check while shown, so a tab left open hides the banner once the time passes.
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const id = setInterval(() => setNow(Date.now()), RECHECK_MS);
    return () => clearInterval(id);
  }, [active]);

  if (!notice) {
    return null;
  }

  return (
    <div
      role="status"
      className="fixed top-0 inset-x-0 z-[9999] text-center text-sm text-yellow-100 bg-yellow-950 border-b border-yellow-700 px-4 py-3"
    >
      {notice.message} We expect to be back by {formatLocalTime(notice.until)}.
    </div>
  );
};

export default MaintenanceBanner;
